import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

type MerchItem = {
  id: string;
  category: string;
  item_name: string;
  is_kid: boolean | null;
  price: number | null;
  item_group?: string | null;
  group_precedence?: number | null;
  archived: boolean;
};

type MerchSize = {
  id: string;
  size_name: string;
  is_kid: boolean | string | number | null;
  is_shoe?: boolean | string | number | null;
  precedence?: number | null;
};

type MerchItemSize = {
  id: string;
  item_id: string;
  size_id: string;
  archived: boolean;
};

type MerchItemImage = {
  id: string;
  item_id: string;
  om_id: string;
  archived: boolean;
};

type MediaRow = Tables<"object_media">;

type ItemCategory = "ADULT" | "CHILD" | "SHOE";

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  ADULT: "Adult",
  CHILD: "Child",
  SHOE: "Footwear",
};

export default function MembersMerchandise() {
  const [items, setItems] = useState<MerchItem[]>([]);
  const [sizes, setSizes] = useState<MerchSize[]>([]);
  const [itemSizes, setItemSizes] = useState<MerchItemSize[]>([]);
  const [itemImages, setItemImages] = useState<MerchItemImage[]>([]);
  const [merchMedia, setMerchMedia] = useState<MediaRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchMerchandiseMedia = async () => {
    try {
      const { data: albumData, error: albumError } = await supabase
        .from("object_media_album")
        .select("id,album_name,album_string")
        .or(
          "album_string.eq.merchandise,album_name.ilike.Merch%,album_name.ilike.March%",
        );

      if (albumError) {
        console.error("Error loading Merchandise album:", albumError);
        setMerchMedia([]);
        return;
      }

      const album = (albumData as any[] | null)?.[0];
      if (!album) {
        setMerchMedia([]);
        return;
      }

      const merchAlbumId = album.id as string;

      const { data: taggedData, error: taggedError } = await (supabase as any)
        .from("object_media_tagged")
        .select("om_id")
        .eq("oma_id", merchAlbumId)
        .eq("archived", false);

      if (taggedError) {
        console.error("Error loading Merchandise-tagged media:", taggedError);
        setMerchMedia([]);
        return;
      }

      const omIds = Array.from(
        new Set(
          ((taggedData as any[] | null) ?? []).map((row) => row.om_id as string),
        ),
      );

      if (omIds.length === 0) {
        setMerchMedia([]);
        return;
      }

      const { data: mediaData, error: mediaError } = await supabase
        .from("object_media")
        .select("*")
        .in("id", omIds)
        .eq("archived", false)
        .eq("is_image", true);

      if (mediaError) {
        console.error("Error loading Merchandise media rows:", mediaError);
        setMerchMedia([]);
        return;
      }

      setMerchMedia((mediaData as any[] as MediaRow[]) ?? []);
    } catch (error) {
      console.error("Unexpected error loading Merchandise media:", error);
      setMerchMedia([]);
    }
  };

  const fetchAll = async () => {
    try {
      setIsLoading(true);

      const [itemsRes, sizesRes, itemSizesRes, itemImagesRes] = await Promise.all([
        supabase.from("merch_cat_item").select("*"),
        supabase
          .from("merch_cat_size")
          .select("id,size_name,is_kid,is_shoe,precedence"),
        supabase.from("merch_cat_item_size").select("*").eq("archived", false),
        supabase.from("merch_cat_item_image").select("*").eq("archived", false),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (sizesRes.error) throw sizesRes.error;
      if (itemSizesRes.error) throw itemSizesRes.error;
      if (itemImagesRes.error) throw itemImagesRes.error;

      setItems((itemsRes.data as any[] as MerchItem[]) ?? []);
      setSizes((sizesRes.data as any[] as MerchSize[]) ?? []);
      setItemSizes((itemSizesRes.data as any[] as MerchItemSize[]) ?? []);
      setItemImages((itemImagesRes.data as any[] as MerchItemImage[]) ?? []);

      await fetchMerchandiseMedia();
    } catch (error) {
      console.error("Error loading merchandise data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isFlagTrue = (value: any) =>
    value === true || value === "true" || value === 1 || value === "1";

  const sortSizes = (list: MerchSize[]): MerchSize[] => {
    return [...list].sort((a, b) => {
      const ap = a.precedence ?? 9999;
      const bp = b.precedence ?? 9999;
      if (ap !== bp) return ap - bp;
      return a.size_name.localeCompare(b.size_name);
    });
  };

  const groupedSizes = useMemo(() => {
    const adult: MerchSize[] = [];
    const child: MerchSize[] = [];
    const footwear: MerchSize[] = [];

    sizes.forEach((s) => {
      const kid = isFlagTrue(s.is_kid);
      const shoe = isFlagTrue(s.is_shoe);

      if (!kid && !shoe) {
        adult.push(s);
      } else if (kid && !shoe) {
        child.push(s);
      } else if (shoe) {
        footwear.push(s);
      }
    });

    return {
      ADULT: sortSizes(adult),
      CHILD: sortSizes(child),
      SHOE: sortSizes(footwear),
    };
  }, [sizes]);

  const getSizesForCategory = (category: ItemCategory): MerchSize[] =>
    groupedSizes[category] ?? [];

  const getActiveSizeIdsForItem = (itemId: string): Set<string> => {
    const set = new Set<string>();
    itemSizes.forEach((rel) => {
      if (rel.item_id === itemId && !rel.archived) {
        set.add(rel.size_id);
      }
    });
    return set;
  };

  const getImagesForItem = (itemId: string): MediaRow[] => {
    if (!itemImages.length || !merchMedia.length) return [];
    const mediaById = new Map(merchMedia.map((m) => [m.id, m]));
    return itemImages
      .filter((rel) => rel.item_id === itemId && !rel.archived)
      .map((rel) => mediaById.get(rel.om_id))
      .filter((m): m is MediaRow => Boolean(m));
  };

  const visibleItems = useMemo(
    () => items.filter((item) => !item.archived),
    [items],
  );

  type DisplayRow =
    | { type: "group"; groupName: string | null }
    | { type: "item"; item: MerchItem };

  const displayRows: DisplayRow[] = useMemo(() => {
    const groups = new Map<string | null, MerchItem[]>();

    visibleItems.forEach((item) => {
      const key = item.item_group ?? null;
      const groupItems = groups.get(key) ?? [];
      groupItems.push(item);
      groups.set(key, groupItems);
    });

    const groupEntries = Array.from(groups.entries()).sort(([a], [b]) => {
      const nameA = a ?? "zzz";
      const nameB = b ?? "zzz";
      return nameA.localeCompare(nameB);
    });

    const rows: DisplayRow[] = [];

    groupEntries.forEach(([groupName, groupItems]) => {
      rows.push({ type: "group", groupName });

      const sortedItems = [...groupItems].sort((a, b) => {
        const ap = a.group_precedence ?? 9999;
        const bp = b.group_precedence ?? 9999;
        if (ap !== bp) return ap - bp;
        return a.item_name.localeCompare(b.item_name);
      });

      sortedItems.forEach((item) => {
        rows.push({ type: "item", item });
      });
    });

    return rows;
  }, [visibleItems]);

  const renderItemRow = (item: MerchItem) => {
    const category = (item.category?.toUpperCase() as ItemCategory) || "ADULT";
    const availableSizes = getSizesForCategory(category);
    const activeSizeIds = getActiveSizeIdsForItem(item.id);
    const sizesForItem = availableSizes.filter((s) => activeSizeIds.has(s.id));
    const imagesForItem = getImagesForItem(item.id);

    return (
      <TableRow key={item.id}>
        <TableCell className="align-top min-w-[260px]">
          <div className="font-medium text-gray-900">{item.item_name}</div>
          {imagesForItem.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {imagesForItem.map((media) => {
                const rawName = media.name ?? media.url.split("/").pop() ?? "";
                const baseName = rawName.includes(".")
                  ? rawName.slice(0, rawName.lastIndexOf("."))
                  : rawName;
                const displayName = baseName.replace(/_/g, " ");

                return (
                  <Dialog key={media.id}>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className="h-10 w-10 overflow-hidden rounded border bg-slate-50"
                      >
                        <img
                          src={media.url}
                          alt={displayName || "Merch item image"}
                          className="h-full w-full object-cover"
                        />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                      <DialogHeader>
                        <DialogTitle className="text-sm">
                          {displayName || "Merch item image"}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="mt-2">
                        <img
                          src={media.url}
                          alt={displayName || "Merch item image"}
                          className="w-full max-h-[70vh] object-contain rounded bg-slate-100"
                        />
                      </div>
                    </DialogContent>
                  </Dialog>
                );
              })}
            </div>
          )}
        </TableCell>
        <TableCell className="align-top min-w-[220px]">
          <div className="mb-1 text-sm font-medium text-gray-800">
            {CATEGORY_LABELS[category] ?? category}
          </div>
          {sizesForItem.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {sizesForItem.map((size) => (
                <span
                  key={size.id}
                  className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800"
                >
                  {size.size_name}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">
              Sizes coming soon.
            </div>
          )}
        </TableCell>
        <TableCell className="align-top min-w-[120px]">
          <div className="text-sm font-semibold text-gray-900">
            {item.price != null ? `£${item.price.toFixed(2)}` : "See instructor"}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading available merchandise...
      </p>
    );
  }

  if (visibleItems.length === 0) {
    return (
      <div className="bg-gray-50 rounded p-6 text-center">
        <p className="text-gray-500">
          Merchandise will appear here once items have been added.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="bg-gray-100 text-taekwondo-red min-w-[260px]">
              Item
            </TableHead>
            <TableHead className="bg-gray-100 text-taekwondo-red min-w-[220px]">
              Category &amp; Sizes
            </TableHead>
            <TableHead className="bg-gray-100 text-taekwondo-red min-w-[120px]">
              Price
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayRows.map((row, index) =>
            row.type === "group" ? (
              <TableRow key={`group-${row.groupName ?? "ungrouped"}-${index}`}>
                <TableCell
                  colSpan={3}
                  className="bg-slate-50 font-semibold text-taekwondo-red"
                >
                  {row.groupName || "Other Merchandise"}
                </TableCell>
              </TableRow>
            ) : (
              renderItemRow(row.item)
            ),
          )}
        </TableBody>
      </Table>
    </div>
  );
}


